import React, { useState } from 'react';
import styles from './index.module.scss';
import ChipList from '@components-clean/core/chip-list';
import { useUnsavedChangesConfirmation } from '@hooks-clean/index';
// eslint-disable-next-line max-len
import { COMPONENT_NAME as EDIT_REVIEW_PLAN_SECTION_COMPONENT_NAME } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/_shared/review-plan-details';
// eslint-disable-next-line max-len
import { COMPONENT_NAME as SUMMARY_SECTION_COMPONENT_NAME } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/_shared/review-summary-card';
import { useTranslation } from '@hooks/index';
import { ModuleNames } from '@utils/constants';

export type SMActiveReviewTabTab = 'review-plan' | 'activity' | 'summary';

type SMActiveReviewTabTabDefinition = {
    /* For getting translated label */
    translationKey: string;
    value: SMActiveReviewTabTab;
};

const tabs: SMActiveReviewTabTabDefinition[] = [
    {
        translationKey: 'sm:monitoringToolkit.activeReviewTab.tabs.reviewPlan.title',
        value: 'review-plan',
    },
    {
        translationKey: 'sm:monitoringToolkit.activeReviewTab.tabs.activity.title',
        value: 'activity',
    },
    {
        translationKey: 'sm:monitoringToolkit.activeReviewTab.tabs.summary.title',
        value: 'summary',
    },
] as const;

type Props = {
    reviewRecordId: number;
    selectedTab: SMActiveReviewTabTab;
    setSelectedTab: React.Dispatch<React.SetStateAction<SMActiveReviewTabTab>>;
};

// Component names to watch for changes (to show unsaved changes confirmation)
const COMPONENT_NAMES = [EDIT_REVIEW_PLAN_SECTION_COMPONENT_NAME, SUMMARY_SECTION_COMPONENT_NAME];

const SMActiveReviewTabActionBar = ({ reviewRecordId, selectedTab, setSelectedTab }: Props) => {
    const { t } = useTranslation('sm');

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAMES,
    });

    // *** Remove if/when KendoUI fix this bug ***
    // Due to a KendoUI bug, when the ChipList rerenders, the last clicked Chip focuses itself...
    // To get around this, we change the key when the tab changes, so that a fresh ChipList is rendered (with no last clicked Chip)
    const [key, setKey] = useState(0);

    return (
        <div className={styles.actionBar}>
            <div className={styles.titleContainer}>
                {t('monitoringToolkit.activeReview')}: {`${ModuleNames.CM}-${reviewRecordId}`}
            </div>
            <ChipList
                key={key}
                data-testid="sm-active-review-tabs"
                className={styles.tabsContainer}
                value={selectedTab}
                onChange={async (e) => {
                    setKey((k) => k + 1);

                    // If already-selected tab clicked, do nothing...
                    const newTab = e.value as SMActiveReviewTabTab | null;
                    if (newTab == null || newTab === selectedTab) {
                        return;
                    }

                    const confirmed = await confirmLossOfChanges();
                    if (!confirmed) {
                        return;
                    }

                    setSelectedTab(newTab);
                }}
                data={tabs.map((tab) => ({
                    text: t(tab.translationKey),
                    value: tab.value,
                }))}
            />
        </div>
    );
};

export default SMActiveReviewTabActionBar;
